import { Component, inject } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LetDirective } from '@ngrx/component';
import ObjectId from 'bson-objectid';
import { BehaviorSubject } from 'rxjs';

import { Company } from '@ninety/platform/models/company/company';
import { HelpfulService } from '@ninety/platform/services';
import { CompanyService } from '@ninety/platform/state';
import { TerraFormFieldModule, TerraTextInputModule, TerraIconModule, TerraErrorModule } from '@ninety/terra';
import { ButtonComponent, CardComponent, CardHeaderComponent } from '@ninety/ui/core';

@Component({
  selector: 'ninety-export-csv',
  templateUrl: './export-csv.component.html',
  styleUrls: ['../_shared/tab-styles.scss', './export-csv.component.scss'],
  standalone: true,
  imports: [
    LetDirective,
    CardComponent,
    CardHeaderComponent,
    TerraFormFieldModule,
    TerraTextInputModule,
    FormsModule,
    ReactiveFormsModule,
    TerraIconModule,
    TerraErrorModule,
    ButtonComponent,
  ],
})
export class ExportCsvComponent {
  private readonly helpfulService = inject(HelpfulService);
  private readonly companyService = inject(CompanyService);

  private readonly _csvExportResult$ = new BehaviorSubject<string>('');
  protected readonly csvExportResult$ = this._csvExportResult$.asObservable();

  companyIdToExportSearch = new FormControl('', {
    validators: [
      (c: AbstractControl): ValidationErrors =>
        !c.value || ObjectId.isValid(c.value) ? null : { isObjectId: { value: c.value } },
    ],
  });

  companyToExport: Company;

  findCompanyById() {
    const companyId = this.companyIdToExportSearch.value.trim();

    if (!ObjectId.isValid(companyId)) {
      this.companyIdToExportSearch.markAsTouched();
      this.companyIdToExportSearch.updateValueAndValidity();
      return;
    }

    this.companyService.getCompanyById(companyId).subscribe({
      next: company => (this.companyToExport = company),
    });
  }

  exportSeatsCsv(): void {
    if (!this.companyToExport) return;
    this.helpfulService.exportSeatsCsv(this.companyToExport._id, this.companyToExport.name).subscribe({
      next: () =>
        this._csvExportResult$.next(`CSV export successful. ${this.companyToExport.name} ${this.companyToExport._id}`),
      error: (err: unknown) =>
        this._csvExportResult$.next(`CSV export failed.\n${err instanceof Error ? err.message : err}`),
    });
  }

  exportDirectoryCsv(): void {
    if (!this.companyToExport) return;
    this.helpfulService.exportDirectoryCsv(this.companyToExport._id, this.companyToExport.name).subscribe({
      next: () =>
        this._csvExportResult$.next(`CSV export successful. ${this.companyToExport.name} ${this.companyToExport._id}`),
      error: (err: unknown) =>
        this._csvExportResult$.next(`CSV export failed.\n${err instanceof Error ? err.message : err}`),
    });
  }
}
